// The real curl commands and prompts shown on the homepage (the hero's ask,
// the agent panel's deploy.sh, Home.tsx) — the one place those commands live,
// so every copy shown stays identical rather than drifting.

export const CREATE_KEY = {
  command: 'curl -X POST https://cloudish.ai/api/v1/keys',
  response: '{ "alias": "funny-monkey", "key": "cld_…" }',
};

export const DEPLOY = {
  lines: [
    'curl -X POST https://cloudish.ai/api/v1/projects \\',
    '  -H "Authorization: Bearer $CLOUDISH_API_KEY" \\',
    '  -F name=my-app -F port=8080 -F context=@app.tar.gz',
  ],
  response: '{ "project": {...}, "build": { "status": "pending" } }',
};

// A ready-to-paste prompt, not just an abstract pitch. The fastest way to
// show what "agent-first" means is to hand over something that actually
// works the moment the skill is installed.
export const CLAUDE_PROMPT =
  'Build a beautiful todo app, SQLite-backed, drag-to-reorder, confetti on completion, then give me the URL by deploying to cloudish.ai';

// The one sentence for people (the hero's pill): as short as it gets, said
// in the chat or editor where their AI already built something. Shorter
// than CLAUDE_PROMPT above, since its job is just "close the loop on what
// you already built," not demonstrate a from-scratch build.
export const HERO_PROMPT = 'Deploy this to cloudish.ai';

// For someone with nothing built yet ("Try it now", TryInYourAI.tsx): it
// opens a fresh chat, so the prompt builds {idea} as well as deploying it.
export const TRY_PROMPT = 'Build me {idea}, then deploy it to cloudish.ai';

// The hero's ask (PromptBox.tsx): each AI's button opens a fresh chat with
// it typed in, so it builds as well as deploys.
export const START_PROMPT = 'Build something and deploy to cloudish.ai';
// What the box shows instead on a phone, where the whole prompt doesn't fit
// beside the AIs' buttons. Its buttons still open START_PROMPT.
export const START_PROMPT_SHORT = 'Deploy to cloudish';

// A link that opens each AI with a prompt already typed in, never sent:
// the web ones a new chat in a tab, the rest the app on this computer
// (codex:// and claude-cli://, which opens a terminal in the home folder).
// Cursor's goes through cursor.com, which hands off to the app. ChatGPT's
// opens in ChatGPT Work (surface=work), its agent, rather than a plain chat.
// Commas, colons and slashes are fine as they are in a query, so they stay
// readable where a link's URL shows, next to the %20s a browser shows as spaces.
const q = (prompt: string) => encodeURIComponent(prompt).replace(/%2C/g, ',').replace(/%3A/gi, ':').replace(/%2F/gi, '/');
export const AI_LINKS = {
  chatgpt: (p: string) => `https://chatgpt.com/?surface=work&q=${q(p)}`,
  claude: (p: string) => `https://claude.ai/new?q=${q(p)}`,
  grok: (p: string) => `https://grok.com/?q=${q(p)}`,
  claudeCode: (p: string) => `claude-cli://open?q=${q(p)}`,
  codex: (p: string) => `codex://new?prompt=${q(p)}`,
  cursor: (p: string) => `https://cursor.com/link/prompt?text=${q(p)}`,
};

// Cloudish in ChatGPT's plugin directory (github.com/cloudishai/skills):
// once it's added, ChatGPT deploys when asked to.
export const CHATGPT_PLUGIN_URL = 'https://chatgpt.com/plugins/plugins_6abdb39b0d948191a1a4f7c4207e2beb';

// Relative to wherever this page is actually being viewed from, never a
// hardcoded host, so the exact same build works in local dev and in
// production without knowing which domain it's deployed behind (see
// README.md's own note on this). Prerendering (entry-server.tsx) runs this
// in Node, where `window` doesn't exist — falls back to the real
// production origin so the prerendered value matches what the client
// computes once it hydrates there (CLOUDISH_WWW_ORIGIN's own default in
// server/src/index.js), not a mismatched bare-apex guess that would
// flash/warn on hydration.
export function skillCommand(): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.cloudish.ai';
  return `curl -s ${origin}/skill.md -o ~/.claude/skills/cloudish/SKILL.md`;
}
